import { useMemo, useRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { KpiCard, BtnLink, Btn, Tag, Banner } from "../components/index.js";
import { Frame } from "../components/Frame.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { Bookshelf, type ShelfDensity, type ShelfSort } from "../components/Bookshelf.js";
import { useAsync } from "../lib/useAsync.js";
import { manuals as manualsApi, books as booksApi, stores as storesApi, hasRole, mediaUrl } from "../lib/api.js";
import { isOrphanDraft } from "../lib/manualFilters.js";
import { hydrateProgress } from "../lib/readingProgress.js";
import type { Book, Manual, ManualStatus, Store } from "@tebiki/shared";

const STATUS_FILTERS: { key: "all" | ManualStatus; label: string }[] = [
  { key: "all", label: "すべて" },
  { key: "published", label: "公開中" },
  { key: "draft", label: "下書き" },
  { key: "review", label: "承認待ち" },
];

type LibraryData = [Manual[], Book[], Store[]];

/**
 * ライブラリ = 書架: REAL 本 entities on shelves + a 未整理トレイ for manuals not in any book.
 * 「＋新しい本」creates a book inline; contents are curated per-manual (提出シート / Preview /
 * BookView). Filtering by status/検索 switches to a flat all-manuals list so nothing hides.
 */
export function Library() {
  const canEdit = hasRole("owner", "admin", "editor");
  const location = useLocation();
  // 提出/公開直後の着地フィードバック（Preview の提出シートが state で渡す）。
  const submitted = (location.state as { submitted?: { mode: string; title: string } } | null)?.submitted;
  const [showSubmitted, setShowSubmitted] = useState(!!submitted);
  // 別デバイスで読んだ進捗を一度だけ取り込む（リングと修了の同期）。
  useEffect(() => {
    void hydrateProgress();
  }, []);
  const { data, loading, error, offline, reload } = useAsync<LibraryData>(
    async () => {
      const [list, bookList, stores] = await Promise.all([
        manualsApi.list().then((r) => r.items.filter((m) => !isOrphanDraft(m.status, m.title))),
        booksApi.list().catch(() => [] as Book[]),
        storesApi.list().catch(() => [] as Store[]),
      ]);
      return [list, bookList, stores];
    },
    [],
  );

  const [allItems, books, stores] = data ?? [[], [], []];
  // 現場閲覧者には公開済みのみ（承認前の内容を見せない）。APIも同じ方針でフィルタ済みだが、
  // 混在ロールに備えてUI側でも明示する。
  const viewerOnly = !hasRole("owner", "admin", "editor", "reviewer");
  const items = useMemo(
    () => (viewerOnly ? allItems.filter((m) => m.status === "published") : allItems),
    [allItems, viewerOnly],
  );

  const [q, setQ] = useState("");
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]["key"]>("all");
  const [density, setDensity] = useState<ShelfDensity>("cover");
  const [sort, setSort] = useState<ShelfSort>("recent");

  // ---- ＋新しい本 (inline create) ----
  const [creating, setCreating] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [busyCreate, setBusyCreate] = useState(false);
  const createRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (creating) createRef.current?.focus();
  }, [creating]);
  const newBookBtnRef = useRef<HTMLButtonElement>(null);
  /** Close the inline create form; focus returns to the trigger (never dropped on body). */
  function closeCreate() {
    setCreating(false);
    setTitleDraft("");
    requestAnimationFrame(() => newBookBtnRef.current?.focus());
  }
  async function createBook() {
    const title = titleDraft.trim();
    if (!title || busyCreate) return;
    setBusyCreate(true);
    try {
      await booksApi.create({ title });
      closeCreate();
      await reload();
    } catch {
      alert("本を作成できませんでした。通信環境を確認してください。");
    } finally {
      setBusyCreate(false);
    }
  }

  const counts = useMemo(
    () => ({
      published: items.filter((m) => m.status === "published").length,
      review: items.filter((m) => m.status === "review").length,
      draft: items.filter((m) => m.status === "draft").length,
    }),
    [items],
  );

  const inAnyBook = useMemo(() => new Set(books.flatMap((b) => b.manualIds)), [books]);
  const unfiled = useMemo(() => items.filter((m) => !inAnyBook.has(m.manualId)), [items, inAnyBook]);

  const filteredBooks = useMemo(
    () => (q ? books.filter((b) => `${b.title} ${b.description ?? ""}`.toLowerCase().includes(q.toLowerCase())) : books),
    [books, q],
  );

  // 検索に一致するマニュアル（本の中身も含む横断検索 — 本タイトルしか引けないと収載済みが行方不明になる）。
  const searchedManuals = useMemo(
    () =>
      q
        ? items.filter((m) => {
            const hay = `${m.title} ${(m.meta?.tags ?? []).join(" ")} ${m.meta?.category ?? ""}`.toLowerCase();
            return hay.includes(q.toLowerCase());
          })
        : [],
    [items, q],
  );

  // Status filter switches to a flat manual list.
  const flatMode = status !== "all";
  const flatItems = useMemo(
    () =>
      items.filter((m) => {
        if (status !== "all" && m.status !== status) return false;
        if (q) {
          const hay = `${m.title} ${(m.meta?.tags ?? []).join(" ")} ${m.meta?.category ?? ""}`.toLowerCase();
          if (!hay.includes(q.toLowerCase())) return false;
        }
        return true;
      }),
    [items, status, q],
  );

  const filtersActive = q !== "" || status !== "all";
  function resetFilters() {
    setQ("");
    setStatus("all");
  }

  return (
    <ConsoleShell
      breadcrumb={["TEBIKI", "書架"]}
      actions={
        canEdit ? (
          <BtnLink to="/capture/video" state={{ from: "/library" }} variant="primary" icon={<Icon name="capture" />}>
            撮影して作成
          </BtnLink>
        ) : undefined
      }
    >
      <PageHead title={<>マニュアル<span className="hl">書架</span></>}>
        本に束ねて、対象の店舗・スタッフへ届ける本棚。全 {items.length} 件 / {books.length} 冊の本。
      </PageHead>

      {showSubmitted && submitted ? (
        <Banner variant={submitted.mode === "publish" ? "info" : "ai"} icon={<Icon name={submitted.mode === "publish" ? "check" : "approval"} />} className="reveal">
          <span className="jp grow">
            {submitted.mode === "publish" ? (
              <>「<b>{submitted.title}</b>」を公開しました。対象スタッフの書架に並びます。</>
            ) : submitted.mode === "save" ? (
              <>「<b>{submitted.title}</b>」の変更を保存しました。</>
            ) : (
              <>「<b>{submitted.title}</b>」を承認に出しました。承認されると公開されます。</>
            )}
          </span>
          <button className="tag" type="button" onClick={() => setShowSubmitted(false)} aria-label="このお知らせを閉じる">
            <Icon name="close" size={13} />
          </button>
        </Banner>
      ) : null}

      <div className="grid g-3" style={{ marginBottom: 22 }}>
        <KpiCard label="公開中" value={counts.published} className="reveal" />
        <KpiCard label="承認待ち" value={counts.review} suffix="件" className="reveal d1" />
        <KpiCard label="下書き" value={counts.draft} className="reveal d2" />
      </div>

      <div className="toolbar" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 18 }}>
        <div className="search" style={searchStyle}>
          <span style={{ color: "var(--muted)", display: "inline-flex" }} aria-hidden="true">
            <Icon name="search" size={16} />
          </span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="本・マニュアルを検索…"
            className="jp"
            aria-label="本・マニュアルを検索"
            style={{ flex: 1, background: "none", border: 0, padding: "13px 0", color: "var(--ink)", fontSize: 16, outline: "none", fontFamily: "var(--font-ui)" }}
          />
        </div>
        <div className="segmented" role="group" aria-label="ステータスで絞り込み">
          {STATUS_FILTERS.map((f) => (
            <button key={f.key} aria-pressed={status === f.key} onClick={() => setStatus(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="segmented" role="group" aria-label="本の表示">
          <button aria-pressed={density === "cover"} onClick={() => setDensity("cover")} title="表紙で見る">表紙</button>
          <button aria-pressed={density === "spine"} onClick={() => setDensity("spine")} title="背表紙で詰めて見る">背</button>
        </div>
        <select className="select" style={{ width: "auto" }} aria-label="並び替え" value={sort} onChange={(e) => setSort(e.target.value as ShelfSort)}>
          <option value="recent">新着順</option>
          <option value="name">名前順</option>
          <option value="count">冊数順</option>
        </select>
        {canEdit ? (
          creating ? (
            <span className="row" style={{ gap: 6, border: "1px dashed var(--line-2)", borderRadius: 12, padding: "4px 6px 4px 12px" }}>
              <input
                ref={createRef}
                className="jp"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void createBook();
                  else if (e.key === "Escape") closeCreate();
                }}
                placeholder="新しい本のタイトル"
                aria-label="新しい本のタイトル"
                style={{ border: 0, background: "none", outline: "none", color: "var(--ink)", fontSize: 14, width: 180 }}
              />
              <Btn onClick={() => void createBook()} disabled={!titleDraft.trim() || busyCreate}>
                {busyCreate ? "作成中…" : "作成"}
              </Btn>
              <button type="button" className="tag" aria-label="本の作成をやめる" onClick={closeCreate} style={{ cursor: "pointer", color: "var(--muted)" }}>
                <Icon name="close" size={13} />
              </button>
            </span>
          ) : (
            <button ref={newBookBtnRef} className="btn" onClick={() => setCreating(true)}>
              <span className="ico" aria-hidden="true"><Icon name="plus" size={16} /></span>
              新しい本
            </button>
          )
        ) : null}
      </div>

      {loading ? (
        <LoadingState label="書架を読み込み中…" />
      ) : error ? (
        <ErrorState message={error} offline={offline} onRetry={reload} />
      ) : flatMode ? (
        // ---- flat filtered list (status filter engaged) ----
        flatItems.length === 0 ? (
          <EmptyState icon={<Icon name="search" size={30} />} title="該当するマニュアルがありません">
            検索条件を変えてください。
            {filtersActive ? (
              <div style={{ marginTop: 16 }}>
                <Btn onClick={resetFilters} icon={<Icon name="close" size={16} />}>絞り込みを解除</Btn>
              </div>
            ) : null}
          </EmptyState>
        ) : (
          <ManualRows items={flatItems} canEdit={canEdit} />
        )
      ) : books.length === 0 && items.length === 0 ? (
        <EmptyState icon={<Icon name="library" size={30} />} title="書架はまだ空です">
          {canEdit ? (
            <>
              撮影して最初のマニュアルを作り、本に束ねましょう。
              <div style={{ marginTop: 16 }}>
                <BtnLink to="/capture/video" state={{ from: "/library" }} variant="primary" icon={<Icon name="capture" />}>撮影して作成</BtnLink>
              </div>
            </>
          ) : (
            <>マニュアルが公開されると、ここに本として並びます。</>
          )}
        </EmptyState>
      ) : (
        <>
          {filteredBooks.length === 0 ? (
            q ? (
              <EmptyState icon={<Icon name="search" size={30} />} title="該当する本がありません">
                {searchedManuals.length > 0 ? "下の検索結果（マニュアル）を確認してください。" : "検索語を変えてみてください。"}
              </EmptyState>
            ) : (
              <EmptyState icon={<Icon name="library" size={30} />} title="まだ本がありません">
                {canEdit
                  ? "「＋新しい本」で本を作ると、マニュアルを束ねて対象の店舗・スタッフに届けられます。"
                  : "本が届くとここに並びます。"}
              </EmptyState>
            )
          ) : (
            <Bookshelf books={filteredBooks} manuals={items} stores={stores} density={density} sort={sort} />
          )}

          {q && searchedManuals.length > 0 ? (
            // 横断検索: 本に収載済みのマニュアルも検索で見つかるように（本タイトル検索だけだと行方不明になる）
            <section aria-label="検索に一致するマニュアル" style={{ marginTop: 34 }}>
              <div className="spread" style={{ marginBottom: 10, alignItems: "baseline" }}>
                <h2 className="display" style={{ fontSize: 22 }}>検索結果のマニュアル</h2>
                <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
                  「{q}」に一致 {searchedManuals.length}件（本の中身も含む）
                </span>
              </div>
              <ManualRows items={searchedManuals} canEdit={canEdit} />
            </section>
          ) : unfiled.length > 0 ? (
            <section aria-label="未整理のマニュアル" style={{ marginTop: 34 }}>
              <div className="spread" style={{ marginBottom: 10, alignItems: "baseline" }}>
                <h2 className="display" style={{ fontSize: 22 }}>未整理トレイ</h2>
                <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
                  {canEdit
                    ? `どの本にも入っていないマニュアル ${unfiled.length}件 — 開いて「提出する」から本に入れられます`
                    : `どの本にも入っていないマニュアル ${unfiled.length}件`}
                </span>
              </div>
              <ManualRows items={unfiled} canEdit={canEdit} />
            </section>
          ) : null}
        </>
      )}
    </ConsoleShell>
  );
}

/** Flat manual rows (used by the status-filtered view and the 未整理トレイ). */
function ManualRows({ items, canEdit }: { items: Manual[]; canEdit: boolean }) {
  if (items.length === 0)
    return (
      <div className="jp" style={{ padding: "20px 4px", color: "var(--muted)", fontSize: 14 }}>
        該当なし。
      </div>
    );
  return (
    <div className="list reveal d1">
      {items.map((m, i) => (
        <div key={m.manualId} className="lrow" style={{ gridTemplateColumns: "56px 1fr auto auto", alignItems: "center" }}>
          <Frame paper className="thumb" style={{ width: 56 }} src={mediaUrl(m.thumbnailUrl)} subject={m.status === "published"} />
          <div style={{ minWidth: 0 }}>
            <div className="display jp" style={{ fontSize: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {m.title}
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
              {m.sourceType === "video" ? "動画" : "画像"} · v{m.version ?? 1}
              {m.meta?.category ? ` · ${m.meta.category}` : ""}
            </div>
          </div>
          <span>
            {m.status === "published" ? (
              <Tag dot color="var(--ok-ink)">公開中</Tag>
            ) : m.status === "review" ? (
              <Tag hi>承認待ち</Tag>
            ) : m.status === "processing" ? (
              <Tag dot color="var(--info-ink)">AI処理中</Tag>
            ) : (
              <Tag>下書き</Tag>
            )}
          </span>
          <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
            {m.status === "processing" ? (
              <BtnLink to={`/processing/${m.manualId}`}>処理を見る</BtnLink>
            ) : (
              <>
                <BtnLink to={`/read/${m.manualId}`} state={{ from: "/library" }} icon={<Icon name="play" size={15} />}>読む</BtnLink>
                {canEdit ? (
                  <Link className="tag" to={`/preview/${m.manualId}`} state={{ from: "/library" }} title="開いて整える" aria-label={`${m.title} を開いて整える`}>
                    <Icon name="edit" size={15} />
                  </Link>
                ) : null}
              </>
            )}
          </div>
          {i < items.length - 1 ? null : null}
        </div>
      ))}
    </div>
  );
}

const searchStyle = {
  flex: 1,
  minWidth: 220,
  display: "flex",
  alignItems: "center",
  gap: 10,
  background: "var(--bg2)",
  border: "1px solid var(--line-2)",
  borderRadius: 12,
  padding: "0 14px",
} as const;
